/**
 * Agent Chat Service
 * Handles communication with the Azure AI Agent backend API 
 * Supports MCP approval flow as per backend specification
 */

import api from '../api/config';

const AGENT_API_ENDPOINT = '/api/agent/chat';
const TIMEOUT_MS = 120000; // 120 seconds timeout for long-running operations

/**
 * Get authentication token from localStorage
 * @returns {string|null}
 */
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * Extracts a consistent error message from an API error
 * @param {any} error
 * @returns {string}
 */
const extractErrorMessage = (error) => {
  if (error.response?.data) {
    const data = error.response.data;
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    if (data.error) return data.error;
  }
  
  if (error.response?.status === 401) return "Authentication failed. Please log in again.";
  if (error.response?.status === 403) return "You don't have permission to perform this action.";
  if (error.response?.status === 400) return "Invalid request. Please check your input.";
  if (error.response?.status >= 500) return "A server error occurred. Please try again later.";
  if (error.code === 'ECONNABORTED') return "Request timed out. Please try again.";
  
  return error.message || "An unexpected error occurred. Please try again.";
};

/**
 * Normalize stream chunk to handle backend SSE event types:
 * - {type: "conversation_start", conversation_id: "..."} - conversation start
 * - {type: "conversation_id", conversation_id: "..."} - conversation ID update
 * - {type: "content", content: "..."} - streaming text
 * - {type: "done", full_message: "..."} - stream completion
 * - {type: "mcp_approval_request", approval_request: {...}} - tool approval
 * - {status: "..."} - status update
 * @param {Object} chunk - Raw SSE event data
 * @returns {Object} - Normalized chunk with type and data
 */
const normalizeStreamChunk = (chunk) => {
  if (!chunk || typeof chunk !== 'object') {
    return { type: 'content', data: { content: String(chunk || '') } };
  }

  const eventType = chunk.type;

  // Event: conversation_start
  if (eventType === 'conversation_start') {
    return {
      type: 'conversation_start',
      data: {
        conversation_id: chunk.conversation_id
      }
    };
  }

  // Event: conversation_id (Azure AI agent sends this)
  if (eventType === 'conversation_id') {
    return {
      type: 'conversation_id',
      data: {
        conversation_id: chunk.conversation_id
      }
    };
  }

  // Event: content (streaming text)
  if (eventType === 'content') {
    return {
      type: 'content',
      data: {
        content: chunk.content || ''
      }
    };
  }

  // Event: done (stream completion)
  if (eventType === 'done') {
    return {
      type: 'done',
      data: {
        conversation_id: chunk.conversation_id,
        full_message: chunk.full_message || ''
      }
    };
  }

  // Event: mcp_approval_request
  if (eventType === 'mcp_approval_request') {
    return {
      type: 'mcp_approval_request',
      data: {
        approval_request: {
          id: chunk.approval_request?.id,
          tool_name: chunk.approval_request?.tool_name,
          server_label: chunk.approval_request?.server_label,
          arguments: chunk.approval_request?.arguments,
          response_id: chunk.approval_request?.response_id
        }
      }
    };
  }

  // Event: status update
  if (chunk.status) {
    return {
      type: 'status',
      data: {
        status: chunk.status
      }
    };
  }

  // Unknown event type - log but don't show in UI
  console.warn('Unknown SSE event:', chunk);
  return {
    type: 'unknown',
    data: {}
  };
};

/**
 * Generate a unique message ID
 * @returns {string}
 */
const generateMessageId = () => {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Send a message to the agent
 * @param {string} message
 * @param {string} [conversationId]
 * @returns {Promise<Object>}
 */
export const sendMessage = async (message, conversationId) => {
  try {
    const requestData = {
      message,
      ...(conversationId && { conversation_id: conversationId }),
    };

    const response = await api.post(
      `${AGENT_API_ENDPOINT}/send`,
      requestData,
      { timeout: TIMEOUT_MS }
    );

    return response.data;
  } catch (error) {
    const errorMessage = extractErrorMessage(error);
    throw new Error(errorMessage);
  }
};

/**
 * Send a message to the agent with streaming support
 * Uses Server-Sent Events (SSE) for real-time updates
 * @param {string} message
 * @param {string|null} conversationId
 * @param {Function} onChunk
 * @param {Function} onError
 * @param {Function} onComplete
 * @returns {Promise<Function>} Abort function
 */
export const sendMessageStream = async (
  message,
  conversationId,
  onChunk,
  onError,
  onComplete
) => {
  const token = localStorage.getItem('authToken');
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
    onError('Request timed out after 60 seconds');
  }, TIMEOUT_MS);

  try {
    const baseURL = api.defaults.baseURL || '';
    const response = await fetch(`${baseURL}${AGENT_API_ENDPOINT}/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify({
        message,
        conversation_id: conversationId,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
    }

    if (!response.body) {
      throw new Error('ReadableStream not supported');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              
              if (data === '[DONE]' || data === '') {
                continue;
              }

              try {
                const chunk = JSON.parse(data);
                console.log('Stream chunk received:', chunk);
                
                // Normalize the chunk structure
                const normalizedChunk = normalizeStreamChunk(chunk);
                
                if (normalizedChunk.type === 'error') {
                  onError(normalizedChunk.data?.message || normalizedChunk.message || 'Unknown error');
                } else if (normalizedChunk.type === 'done' || normalizedChunk.type === 'complete') {
                  onComplete(normalizedChunk.data || normalizedChunk);
                } else {
                  onChunk(normalizedChunk);
                }
              } catch (parseError) {
                console.warn('Failed to parse stream chunk:', data, parseError);
                // Try to handle as plain text content
                if (data && data.length > 0) {
                  onChunk({ type: 'content', data: { content: data } });
                }
              }
            } else if (line.startsWith('event: ')) {
              // Handle SSE event type
              console.log('SSE event:', line.slice(7));
            } else if (line.trim() && !line.startsWith(':')) {
              // Handle non-prefixed data (some servers send raw JSON)
              try {
                const chunk = JSON.parse(line);
                console.log('Raw JSON chunk:', chunk);
                const normalizedChunk = normalizeStreamChunk(chunk);
                onChunk(normalizedChunk);
              } catch (e) {
                // Not JSON, might be plain text
                console.log('Non-JSON line:', line);
              }
            }
          }
        }
      } catch (streamError) {
        if (streamError.name !== 'AbortError') {
          onError(extractErrorMessage(streamError));
        }
      }
    };

    processStream();

    // Return abort function
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  } catch (error) {
    clearTimeout(timeoutId);
    const errorMessage = extractErrorMessage(error);
    onError(errorMessage);
    return () => {}; // Return empty cleanup function
  }
};

/**
 * Send MCP approval/rejection and stream the continued response
 * Uses POST /api/agent/chat/stream endpoint with approval payload
 * @param {string} approvalRequestId - The approval request ID
 * @param {boolean} approved - true to approve, false to reject
 * @param {string} responseId - The response ID for resumption
 * @param {string} conversationId - The conversation ID
 * @param {string} approvalMode - "once", "always_this_tool", or "always_all" (only for approval)
 * @param {Function} onChunk - Callback for stream chunks
 * @param {Function} onError - Callback for errors
 * @param {Function} onComplete - Callback for completion
 * @returns {Promise<Function>} - Abort function
 */
export const sendMcpApproval = async (
  approvalRequestId,
  approved,
  responseId,
  conversationId,
  approvalMode,
  onChunk,
  onError,
  onComplete
) => {
  const token = getAuthToken();
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
    onError('Request timed out after 120 seconds');
  }, TIMEOUT_MS);

  try {
    const baseURL = api.defaults.baseURL || '';
    
    // Build payload according to backend spec
    const payload = {
      message: "", // Empty message when approving/denying
      conversation_id: conversationId,
      previous_response_id: responseId
    };
    
    if (approved) {
      payload.approved_tool_call_ids = [approvalRequestId];
      payload.approval_mode = approvalMode || "once";
    } else {
      payload.denied_tool_call_ids = [approvalRequestId];
    }
    
    const response = await fetch(`${baseURL}${AGENT_API_ENDPOINT}/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
    }

    if (!response.body) {
      throw new Error('ReadableStream not supported');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              
              if (data === '[DONE]' || data === '') {
                continue;
              }

              try {
                const chunk = JSON.parse(data);
                console.log('MCP approval stream chunk:', chunk);
                
                const normalizedChunk = normalizeStreamChunk(chunk);
                
                if (normalizedChunk.type === 'error') {
                  onError(normalizedChunk.data?.error || 'Unknown error');
                } else if (normalizedChunk.type === 'done') {
                  onComplete(normalizedChunk.data || normalizedChunk);
                } else {
                  onChunk(normalizedChunk);
                }
              } catch (parseError) {
                console.warn('Failed to parse MCP approval chunk:', data, parseError);
              }
            }
          }
        }
        
        // Stream completed
        if (onComplete) {
          onComplete({});
        }
      } catch (streamError) {
        if (streamError.name !== 'AbortError') {
          onError(extractErrorMessage(streamError));
        }
      }
    };

    processStream();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  } catch (error) {
    clearTimeout(timeoutId);
    const errorMessage = extractErrorMessage(error);
    onError(errorMessage);
    return () => {};
  }
};

/**
 * Confirm or reject an agent action (deprecated - use sendMcpApproval for MCP approvals)
 * @param {string} conversationId
 * @param {boolean} confirmed
 * @param {Object} [confirmationData]
 * @returns {Promise<Object>}
 */
/**
 * Confirm or reject an agent action (deprecated - use sendMcpApproval for MCP approvals)
 * @param {string} conversationId
 * @param {boolean} confirmed
 * @param {Object} [confirmationData]
 * @returns {Promise<Object>}
 */
export const confirmAction = async (conversationId, confirmed, confirmationData) => {
  try {
    const requestData = {
      conversation_id: conversationId,
      confirmed,
      ...(confirmationData && { confirmation_data: confirmationData }),
    };

    const response = await api.post(
      `${AGENT_API_ENDPOINT}/confirm`,
      requestData,
      { timeout: TIMEOUT_MS }
    );

    return response.data;
  } catch (error) {
    const errorMessage = extractErrorMessage(error);
    throw new Error(errorMessage);
  }
};

/**
 * Edit an agent's proposed instruction before execution
 * @param {string} conversationId
 * @param {string} editedInstruction
 * @param {string} [originalInstructionId]
 * @returns {Promise<Object>}
 */
export const editInstruction = async (conversationId, editedInstruction, originalInstructionId) => {
  try {
    const requestData = {
      conversation_id: conversationId,
      edited_instruction: editedInstruction,
      ...(originalInstructionId && { original_instruction_id: originalInstructionId }),
    };

    const response = await api.post(
      `${AGENT_API_ENDPOINT}/edit`,
      requestData,
      { timeout: TIMEOUT_MS }
    );

    return response.data;
  } catch (error) {
    const errorMessage = extractErrorMessage(error);
    throw new Error(errorMessage);
  }
};

/**
 * Get conversation history
 * @param {string} conversationId
 * @returns {Promise<Array>}
 */
export const getConversationHistory = async (conversationId) => {
  try {
    const response = await api.get(
      `${AGENT_API_ENDPOINT}/history/${conversationId}`
    );
    
    return response.data.messages.map(msg => ({
      ...msg,
      timestamp: new Date(msg.timestamp),
    }));
  } catch (error) {
    const errorMessage = extractErrorMessage(error);
    throw new Error(errorMessage);
  }
};

/**
 * Clear/delete a conversation
 * @param {string} conversationId
 * @returns {Promise<void>}
 */
export const clearConversation = async (conversationId) => {
  try {
    await api.delete(`${AGENT_API_ENDPOINT}/conversation/${conversationId}`);
  } catch (error) {
    // Silently fail if conversation doesn't exist
    console.warn('Failed to clear conversation:', extractErrorMessage(error));
  }
};

/**
 * Create a user message object
 * @param {string} content
 * @returns {Object}
 */
export const createUserMessage = (content) => ({
  id: generateMessageId(),
  role: 'user',
  content,
  timestamp: new Date(),
});

/**
 * Create an assistant message object
 * @param {string} content
 * @param {Array} [toolCalls]
 * @returns {Object}
 */
export const createAssistantMessage = (content, toolCalls) => ({
  id: generateMessageId(),
  role: 'assistant',
  content,
  timestamp: new Date(),
  ...(toolCalls && { toolCalls }),
});

/**
 * Create a system message object
 * @param {string} content
 * @returns {Object}
 */
export const createSystemMessage = (content) => ({
  id: generateMessageId(),
  role: 'system',
  content,
  timestamp: new Date(),
});

/**
 * Create a thinking message object
 * @param {string} [content]
 * @returns {Object}
 */
export const createThinkingMessage = (content = 'Thinking...') => ({
  id: generateMessageId(),
  role: 'thinking',
  content,
  timestamp: new Date(),
});

/**
 * Create an error message object
 * @param {string} content
 * @returns {Object}
 */
export const createErrorMessage = (content) => ({
  id: generateMessageId(),
  role: 'error',
  content,
  timestamp: new Date(),
});

/**
 * Create a confirmation message object
 * @param {string} content
 * @param {Object} confirmationData
 * @returns {Object}
 */
export const createConfirmationMessage = (content, confirmationData) => ({
  id: generateMessageId(),
  role: 'confirmation',
  content,
  timestamp: new Date(),
  needsConfirmation: true,
  confirmationData,
});

/**
 * Create a tool execution message object
 * @param {string} toolName
 * @param {'pending' | 'executing' | 'success' | 'error'} status
 * @param {any} [result]
 * @param {string} [error]
 * @returns {Object}
 */
export const createToolExecutionMessage = (toolName, status, result, error) => ({
  id: generateMessageId(),
  role: 'tool_execution',
  content: `Tool: ${toolName}`,
  timestamp: new Date(),
  toolCalls: [{
    id: generateMessageId(),
    name: toolName,
    arguments: {},
    status,
    result,
    error,
  }],
});

// Export default service object
const agentChatService = {
  sendMessage,
  sendMessageStream,
  confirmAction,
  editInstruction,
  getConversationHistory,
  clearConversation,
  createUserMessage,
  createAssistantMessage,
  createSystemMessage,
  createThinkingMessage,
  createErrorMessage,
  createConfirmationMessage,
  createToolExecutionMessage,
};

export default agentChatService;
